import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { emailToUsername, formatDateTime } from '../utils/formatters';
import './TestRunListPage.css';

interface TestRunSummary {
  id: string;
  name: string;
  folder_id: string;
  status: string;
  created_at: string;
  created_by: string;
  total_cases: number;
  pass_count: number;
  fail_count: number;
  not_run_count: number;
  progress_percentage: number;
}

type TabFilter = 'Recent' | 'Scheduled' | 'Archived';

const TestRunListPage: React.FC = () => {
  const navigate = useNavigate();
  const [testRuns, setTestRuns] = useState<TestRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabFilter>('Recent');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTestRuns();
  }, [statusFilter]);

  const loadTestRuns = async () => {
    try {
      setLoading(true);
      const runs = await api.listTestRuns(undefined, statusFilter || undefined);
      setTestRuns(runs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test runs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'NOT_STARTED':
        return { label: 'NOT STARTED', className: 'trl-status-not-started', dot: false };
      case 'IN_PROGRESS':
        return { label: 'RUNNING', className: 'trl-status-running', dot: true };
      case 'COMPLETED':
        return { label: 'COMPLETED', className: 'trl-status-completed', dot: false };
      case 'ABORTED':
        return { label: 'ABORTED', className: 'trl-status-aborted', dot: false };
      default:
        return { label: status, className: 'trl-status-not-started', dot: false };
    }
  };

  const getProgressBarClass = (status: string, failCount: number) => {
    if (status === 'COMPLETED' && failCount > 0) return 'trl-progress-fill trl-progress-fail';
    if (status === 'COMPLETED') return 'trl-progress-fill trl-progress-success';
    if (status === 'IN_PROGRESS') return 'trl-progress-fill trl-progress-running';
    return 'trl-progress-fill trl-progress-empty';
  };

  const getActionLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'View Results';
      case 'IN_PROGRESS': return 'Open Live Log';
      case 'NOT_STARTED': return 'Start Now';
      default: return 'View';
    }
  };

  const getActionClass = (status: string, failCount: number) => {
    if (status === 'COMPLETED' && failCount > 0) return 'trl-action-rerun';
    return 'trl-action-primary';
  };

  const getUserInitials = (email: string) => {
    const name = emailToUsername(email);
    return name.slice(0, 2).toUpperCase();
  };

  const filteredRuns = testRuns; // tab filtering is visual only for now

  if (loading) {
    return (
      <div className="trl-page">
        <div className="trl-loading">Loading test runs...</div>
      </div>
    );
  }

  return (
    <div className="trl-page">
      {/* Page Header */}
      <div className="trl-header">
        <div className="trl-header-left">
          <span className="trl-eyebrow">QUALITY ASSURANCE HUB</span>
          <h1 className="trl-title">Test Runs</h1>
        </div>
        <button className="trl-btn-create" onClick={() => navigate('/test-runs/create')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Create Test Run
        </button>
      </div>

      {error && <div className="trl-error">{error}</div>}

      {/* Toolbar */}
      <div className="trl-toolbar">
        <div className="trl-toolbar-left">
          <span className="trl-filter-label">STATUS:</span>
          <div className="trl-status-select-wrap">
            <select
              className="trl-status-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="NOT_STARTED">Not Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="ABORTED">Aborted</option>
            </select>
            <svg className="trl-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>

          <div className="trl-tabs">
            {(['Recent', 'Scheduled', 'Archived'] as TabFilter[]).map(tab => (
              <button
                key={tab}
                className={`trl-tab ${activeTab === tab ? 'trl-tab-active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="trl-toolbar-right">
          <span className="trl-count-label">
            Showing {filteredRuns.length} of {testRuns.length} runs
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
          </svg>
        </div>
      </div>

      {/* Grid */}
      {filteredRuns.length === 0 ? (
        <div className="trl-empty">
          <div className="trl-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/><polyline points="9 11 12 14 15 11"/>
            </svg>
          </div>
          <p>No test runs found. Create your first test run to get started.</p>
          <button className="trl-btn-create" onClick={() => navigate('/test-runs/create')}>
            + Create Test Run
          </button>
        </div>
      ) : (
        <div className="trl-grid">
          {filteredRuns.map((run) => {
            const statusCfg = getStatusConfig(run.status);
            const actionLabel = getActionLabel(run.status);
            const actionClass = getActionClass(run.status, run.fail_count);
            const initials = getUserInitials(run.created_by);
            const username = emailToUsername(run.created_by);

            return (
              <div key={run.id} className="trl-card" onClick={() => navigate(`/test-runs/${run.id}`)}>
                {/* Card Top */}
                <div className="trl-card-top">
                  <span className={`trl-status-badge ${statusCfg.className}`}>
                    {statusCfg.dot && <span className="trl-status-dot" />}
                    {statusCfg.label}
                  </span>
                  <button
                    className="trl-card-menu"
                    onClick={(e) => e.stopPropagation()}
                    title="More options"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
                    </svg>
                  </button>
                </div>

                {/* Card Title */}
                <div className="trl-card-title-section">
                  <h3 className="trl-card-name">{run.name}</h3>
                  <p className="trl-card-date">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    Created {formatDateTime(run.created_at)}
                  </p>
                </div>

                {/* Progress */}
                <div className="trl-progress-section">
                  <div className="trl-progress-header">
                    <span className="trl-progress-label">Progress</span>
                    <span className="trl-progress-pct">{run.progress_percentage.toFixed(0)}%</span>
                  </div>
                  <div className="trl-progress-track">
                    <div
                      className={getProgressBarClass(run.status, run.fail_count)}
                      style={{ width: `${run.progress_percentage}%` }}
                    />
                  </div>
                </div>

                {/* Metrics */}
                <div className="trl-metrics">
                  <div className="trl-metric">
                    <span className="trl-metric-label">TOTAL</span>
                    <span className="trl-metric-value">{run.total_cases}</span>
                  </div>
                  <div className="trl-metric trl-metric-pass">
                    <span className="trl-metric-label">PASS</span>
                    <span className="trl-metric-value">{run.pass_count}</span>
                  </div>
                  <div className="trl-metric trl-metric-fail">
                    <span className="trl-metric-label">FAIL</span>
                    <span className="trl-metric-value">{run.fail_count}</span>
                  </div>
                  <div className="trl-metric trl-metric-skip">
                    <span className="trl-metric-label">
                      {run.status === 'IN_PROGRESS' ? 'LEFT' : 'SKIP'}
                    </span>
                    <span className="trl-metric-value">{run.not_run_count}</span>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="trl-card-footer">
                  <div className="trl-user">
                    <div className="trl-avatar">{initials}</div>
                    <span className="trl-username">{username}</span>
                  </div>
                  <button
                    className={`trl-action-btn ${actionClass}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/test-runs/${run.id}`); }}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            );
          })}

          {/* "Need a specific run?" promo card */}
          <div className="trl-card trl-card-promo">
            <div className="trl-promo-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <h3 className="trl-promo-title">Need a specific run?</h3>
            <p className="trl-promo-text">
              Schedule a recurring regression suite or trigger a quick smoke test on any branch.
            </p>
            <button
              className="trl-promo-btn"
              onClick={() => navigate('/test-runs/create')}
            >
              Quick Start Guide
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunListPage;
